import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, InlineLink, Surface, Txt } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { selectChangePlan, selectPortfolio, selectRetentionOffers } from '@app/redux';
import { selectConfig } from '@app/redux/config';
import { OFMProductTypes } from '@app/types';

import { useHasOffersToDisplay } from '../../hooks';
import Footer from '../Footer';
import PlanOffers from '../PlanOffers';
import RetentionOffers from '../RetentionOffers';

import styles from './index.module.scss';

enum ConfirmationModal {
  PLAN = 'PLAN',
  RETENTION = 'RETENTION',
}

const Content = ({ handleContinue }: { handleContinue: () => void }) => {
  const { t } = useTranslation('Disconnect');

  const { planOffersData, loading: planOffersLoading } = useSelector(selectChangePlan);

  const { data: retentionOffers, loading: retentionOffersLoading } = useSelector(selectRetentionOffers);

  const { portfolio } = useSelector(selectPortfolio);

  const hasActiveRetentionOffer = useMemo(
    () =>
      portfolio?.portfolioItems?.some(
        (item) => item?.kind === OFMProductTypes.RETENTION || item?.kind === OFMProductTypes.RETENTION_OFFER
      ),
    [portfolio]
  );
  const isLoading = planOffersLoading || retentionOffersLoading;
  const hasOffersToDisplay = useHasOffersToDisplay(retentionOffers, planOffersData, Boolean(hasActiveRetentionOffer));

  const [modal, setModal] = useState<ConfirmationModal | null>(null);

  const closeModal = () => setModal(null);
  const handleSelectPlan = () => setModal(ConfirmationModal.PLAN);
  const handleSelectRetentionOffer = () => setModal(ConfirmationModal.RETENTION);

  return (
    <Surface variant="primary" radius="16px" className={styles['content']}>
      {hasOffersToDisplay || isLoading ? (
        <div className={styles['body']}>
          <PlanOffers
            isModalOpen={modal === ConfirmationModal.PLAN}
            handleSelect={handleSelectPlan}
            closeModal={closeModal}
          />
          {!hasActiveRetentionOffer && (
            <RetentionOffers
              isModalOpen={modal === ConfirmationModal.RETENTION}
              handleSelect={handleSelectRetentionOffer}
              closeModal={closeModal}
            />
          )}
        </div>
      ) : (
        <div className={styles['body']}>
          <Txt variant="bodyLargeBold">{t('retentionOffers.empty.title')}</Txt>
          <div className={styles['customerSupportCta']}>
            <Txt variant="bodySmallRegular" component="span">
              {t('retentionOffers.empty.descriptionStart')}
            </Txt>
            <InlineLink variant="primary" href={`tel:${t('Global:callCustomerSupportPhoneNumber')}`}>
              {t('Global:callCustomerSupportPhoneNumberDisplay')}
            </InlineLink>
            <Txt variant="bodySmallRegular" component="span">
              {t('retentionOffers.empty.descriptionEnd')}
            </Txt>
          </div>
        </div>
      )}
      <Footer>
        <Button onClick={handleContinue}>
          {hasOffersToDisplay ? t('button.skipOfferAndContinue') : t('button.noOfferContinue')}
        </Button>
      </Footer>
    </Surface>
  );
};

export default Content;
